import { z } from 'zod';
/**
 * Diário alimentar (N3, estudo do nutricionista): o paciente marca cada refeição do plano
 * em um toque (comi, comi diferente, pulei), com foto se quiser. O Secretário prepara o
 * comentário da foto no tom do nutricionista, que aprova ou escreve o seu antes de ir para
 * o paciente (Código de Ética CFN 856/2026: o que a IA gera é validado pelo profissional).
 * A adesão é a mesma conta para os dois lados.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
const isoDateTime = z.iso.datetime();
export const DIARY_STATUSES = ['comi', 'diferente', 'pulei'];
export const diaryStatusSchema = z.enum(DIARY_STATUSES);
export const DIARY_STATUS_LABEL = {
    comi: 'Comi o do plano',
    diferente: 'Comi diferente',
    pulei: 'Pulei',
};
/** Foto da refeição: JPEG ou WebP já reduzido no aparelho (até 900 KB). */
export const DIARY_PHOTO_MAX_BYTES = 900 * 1024;
export const createDiaryEntrySchema = z.object({
    day: isoDate,
    /** A refeição do plano; sem plano, só o nome. */
    mealId: z.string().min(1).max(40).nullable(),
    mealName: z.string().trim().min(1).max(40),
    /** A opção do plano que a pessoa seguiu. */
    optionId: z.string().min(1).max(40).nullable().optional(),
    status: diaryStatusSchema,
    note: z.string().trim().max(600).optional(),
    photo: z
        .object({
        mime: z.enum(['image/jpeg', 'image/webp']),
        /** base64, sem o prefixo data:. */
        data: z
            .string()
            .min(100)
            .max(Math.ceil((DIARY_PHOTO_MAX_BYTES * 4) / 3) + 8),
    })
        .optional(),
});
export const diaryCommentSchema = z.object({
    text: z.string(),
    author: z.string(),
    sentAt: isoDateTime,
});
export const diaryEntrySchema = z.object({
    id: z.uuid(),
    day: isoDate,
    mealId: z.string().nullable(),
    mealName: z.string(),
    optionId: z.string().nullable(),
    status: diaryStatusSchema,
    note: z.string(),
    hasPhoto: z.boolean(),
    at: isoDateTime,
    /** O comentário que o nutricionista mandou (o rascunho do Secretário não aparece aqui). */
    comment: diaryCommentSchema.nullable(),
});
/** Do lado da clínica: o mesmo registro, mais o paciente e o rascunho do Secretário. */
export const clinicDiaryEntrySchema = diaryEntrySchema.extend({
    patientId: z.uuid(),
    patientName: z.string(),
    draft: z.string().nullable(),
    /** Já tratado: comentado ou marcado "sem comentário". */
    done: z.boolean(),
});
export const sendDiaryCommentSchema = z.object({
    /** Vazio = sem comentário (sai da fila). */
    text: z.string().trim().max(800),
});
export const adherenceSchema = z.object({
    /** % das refeições do plano marcadas como "comi o do plano", nos dias com registro. */
    percent: z.number().nullable(),
    days: z.array(z.object({
        day: isoDate,
        planned: z.number().int(),
        followed: z.number().int(),
        different: z.number().int(),
        skipped: z.number().int(),
    })),
});
/**
 * Adesão de uma faixa de dias: para cada dia, as refeições do plano (as que têm alimento) e
 * como a pessoa marcou. Dia sem nenhum registro não entra na porcentagem (não sabemos), mas
 * aparece com zero na lista.
 */
export function adherenceOf(plan, entries, days) {
    const planned = plan
        ? plan.meals.filter((meal) => meal.options.some((option) => option.items.length > 0))
        : [];
    const out = [];
    let followed = 0;
    let counted = 0;
    for (const day of days) {
        const mine = entries.filter((entry) => entry.day === day);
        // A última marcação de cada refeição vale (a pessoa pode corrigir).
        const byMeal = new Map();
        for (const entry of mine)
            if (entry.mealId)
                byMeal.set(entry.mealId, entry.status);
        const row = {
            day,
            planned: planned.length,
            followed: [...byMeal.values()].filter((s) => s === 'comi').length,
            different: [...byMeal.values()].filter((s) => s === 'diferente').length,
            skipped: [...byMeal.values()].filter((s) => s === 'pulei').length,
        };
        out.push(row);
        if (mine.length > 0 && planned.length > 0) {
            followed += row.followed;
            counted += planned.length;
        }
    }
    return { percent: counted > 0 ? Math.round((followed / counted) * 100) : null, days: out };
}
/** Os últimos `n` dias até `today`, do mais antigo para o mais novo. */
export function lastDays(today, n) {
    const [y, m, d] = today.split('-').map(Number);
    return Array.from({ length: n }, (_, i) => {
        const date = new Date(Date.UTC(y, m - 1, d - (n - 1 - i)));
        return date.toISOString().slice(0, 10);
    });
}
export const patientDiarySchema = z.object({
    entries: z.array(diaryEntrySchema),
    adherence: adherenceSchema,
});
